<template>
  <div
    class="busca-autocomplete"
    :class="{ 'busca-autocomplete--aberto': listaAberta }"
  >
    <div class="busca-autocomplete-input-wrap">
      <input
        :id="inputId"
        ref="inputEl"
        type="search"
        class="busca-autocomplete-input"
        :class="inputClass"
        :value="textoExibido"
        :placeholder="placeholder"
        :disabled="disabled || loading"
        autocomplete="off"
        role="combobox"
        :aria-expanded="listaAberta ? 'true' : 'false'"
        aria-autocomplete="list"
        :aria-controls="listaId"
        :aria-activedescendant="opcaoAtivaId"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      />
      <button
        v-if="permiteLimpar && modelValue && !disabled && !loading"
        type="button"
        class="busca-autocomplete-limpar"
        tabindex="-1"
        aria-label="Limpar seleção"
        @mousedown.prevent="limparSelecao"
      >
        <i class="fa-solid fa-xmark" aria-hidden="true"></i>
      </button>
    </div>
    <ul
      v-if="listaAberta"
      :id="listaId"
      class="busca-autocomplete-lista"
      role="listbox"
    >
      <li v-if="loading" class="busca-autocomplete-item busca-autocomplete-item--estado" role="option">
        {{ loadingLabel }}
      </li>
      <template v-else>
        <li
          v-if="mostrarOpcaoVazia"
          class="busca-autocomplete-item"
          :class="{ 'busca-autocomplete-item--ativa': indiceDestaque === 0 }"
          role="option"
          :id="`${listaId}-opt-0`"
          @mousedown.prevent="selecionarValor('')"
        >
          <span class="busca-autocomplete-item-label">{{ emptyLabel }}</span>
        </li>
        <li
          v-for="(opt, idx) in opcoesFiltradas"
          :key="chaveOpcao(opt, idx)"
          class="busca-autocomplete-item"
          :class="{ 'busca-autocomplete-item--ativa': indiceDestaque === indiceOpcao(idx) }"
          role="option"
          :id="`${listaId}-opt-${indiceOpcao(idx)}`"
          :style="estiloOpcao(opt)"
          @mousedown.prevent="selecionarOpcao(opt)"
        >
          <span class="busca-autocomplete-item-label">{{ rotuloOpcao(opt) }}</span>
          <span v-if="metaOpcao(opt)" class="busca-autocomplete-item-meta">{{ metaOpcao(opt) }}</span>
        </li>
        <li
          v-if="!mostrarOpcaoVazia && opcoesFiltradas.length === 0"
          class="busca-autocomplete-item busca-autocomplete-item--estado"
          role="option"
        >
          Nenhum resultado
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
let proximoListaId = 0;

export default {
  name: "BuscaAutocomplete",

  props: {
    modelValue: { type: String, default: "" },
    options: { type: Array, default: () => [] },
    valueKey: { type: String, default: "nome" },
    labelFn: { type: Function, default: null },
    metaFn: { type: Function, default: null },
    styleFn: { type: Function, default: null },
    filterFn: { type: Function, default: null },
    placeholder: { type: String, default: "Buscar…" },
    emptyLabel: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    loadingLabel: { type: String, default: "Carregando…" },
    disabled: { type: Boolean, default: false },
    inputId: { type: String, default: "" },
    inputClass: { type: [String, Object, Array], default: "" },
    permiteVazio: { type: Boolean, default: true },
    permiteLimpar: { type: Boolean, default: true },
    maxResultados: { type: Number, default: 50 },
    extraOptions: { type: Array, default: () => [] },
  },

  emits: ["update:modelValue"],

  data() {
    proximoListaId += 1;
    return {
      listaId: `busca-ac-lista-${proximoListaId}`,
      focado: false,
      termoBusca: "",
      listaAberta: false,
      indiceDestaque: -1,
      blurTimer: null,
    };
  },

  computed: {
    todasOpcoes() {
      const base = Array.isArray(this.options) ? this.options : [];
      const extras = Array.isArray(this.extraOptions) ? this.extraOptions : [];
      return [...base, ...extras];
    },
    termoFiltro() {
      return String(this.termoBusca || "").trim().toLowerCase();
    },
    mostrarOpcaoVazia() {
      if (!this.permiteVazio || !this.emptyLabel) return false;
      if (!this.termoFiltro) return true;
      return this.emptyLabel.toLowerCase().includes(this.termoFiltro);
    },
    opcoesFiltradas() {
      const q = this.termoFiltro;
      let list = this.todasOpcoes;
      if (q) {
        const fn = this.filterFn;
        list = list.filter((opt) => {
          if (typeof fn === "function") return fn(opt, q);
          return this.filtrarPadrao(opt, q);
        });
      }
      const max = Number(this.maxResultados) > 0 ? Number(this.maxResultados) : 50;
      return list.slice(0, max);
    },
    totalItensLista() {
      let n = this.opcoesFiltradas.length;
      if (this.mostrarOpcaoVazia) n += 1;
      return n;
    },
    textoExibido() {
      if (this.focado) return this.termoBusca;
      const v = String(this.modelValue || "").trim();
      if (!v) return "";
      const opt = this.encontrarOpcaoPorValor(v);
      if (opt != null) return this.rotuloOpcao(opt);
      return v;
    },
    opcaoAtivaId() {
      if (!this.listaAberta || this.indiceDestaque < 0) return undefined;
      return `${this.listaId}-opt-${this.indiceDestaque}`;
    },
  },

  watch: {
    modelValue() {
      if (!this.focado) this.termoBusca = "";
    },
    opcoesFiltradas() {
      if (this.indiceDestaque >= this.totalItensLista) {
        this.indiceDestaque = this.totalItensLista > 0 ? 0 : -1;
      }
    },
  },

  beforeUnmount() {
    if (this.blurTimer) clearTimeout(this.blurTimer);
  },

  methods: {
    rotuloOpcao(opt) {
      if (typeof opt === "string") return opt;
      if (typeof this.labelFn === "function") return String(this.labelFn(opt) ?? "");
      const vk = this.valueKey;
      return String(opt?.[vk] ?? "");
    },
    metaOpcao(opt) {
      if (typeof this.metaFn !== "function") return "";
      return String(this.metaFn(opt) ?? "").trim();
    },
    estiloOpcao(opt) {
      if (typeof this.styleFn !== "function") return undefined;
      return this.styleFn(opt);
    },
    valorOpcao(opt) {
      if (typeof opt === "string") return opt;
      const vk = this.valueKey;
      const raw = opt?.[vk];
      if (raw != null && String(raw).trim()) return String(raw).trim();
      if (typeof this.labelFn === "function") return String(this.labelFn(opt) ?? "").trim();
      return "";
    },
    filtrarPadrao(opt, q) {
      const label = this.rotuloOpcao(opt).toLowerCase();
      if (label.includes(q)) return true;
      const meta = this.metaOpcao(opt).toLowerCase();
      if (meta && meta.includes(q)) return true;
      const val = this.valorOpcao(opt).toLowerCase();
      return val.includes(q);
    },
    encontrarOpcaoPorValor(valor) {
      const v = String(valor || "").trim();
      if (!v) return null;
      for (const opt of this.todasOpcoes) {
        if (this.valorOpcao(opt) === v) return opt;
      }
      return null;
    },
    chaveOpcao(opt, idx) {
      const v = this.valorOpcao(opt);
      return v ? `${v}-${idx}` : `opt-${idx}`;
    },
    indiceOpcao(idxFiltrado) {
      return this.mostrarOpcaoVazia ? idxFiltrado + 1 : idxFiltrado;
    },
    abrirLista() {
      this.listaAberta = true;
      this.indiceDestaque = this.totalItensLista > 0 ? 0 : -1;
    },
    fecharLista() {
      this.listaAberta = false;
      this.indiceDestaque = -1;
    },
    onFocus() {
      if (this.blurTimer) {
        clearTimeout(this.blurTimer);
        this.blurTimer = null;
      }
      this.focado = true;
      this.termoBusca = String(this.modelValue || "").trim()
        ? this.textoExibido
        : "";
      this.abrirLista();
    },
    onBlur() {
      this.blurTimer = setTimeout(() => {
        this.focado = false;
        this.termoBusca = "";
        this.fecharLista();
      }, 150);
    },
    onInput(e) {
      this.termoBusca = e.target.value;
      this.abrirLista();
    },
    onKeydown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        this.termoBusca = "";
        this.fecharLista();
        this.$refs.inputEl?.blur();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (!this.listaAberta) this.abrirLista();
        if (this.totalItensLista === 0) return;
        const next = this.indiceDestaque < 0 ? 0 : Math.min(this.indiceDestaque + 1, this.totalItensLista - 1);
        this.indiceDestaque = next;
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (!this.listaAberta) this.abrirLista();
        if (this.totalItensLista === 0) return;
        const prev = this.indiceDestaque <= 0 ? this.totalItensLista - 1 : this.indiceDestaque - 1;
        this.indiceDestaque = prev;
        return;
      }
      if (e.key === "Enter") {
        if (!this.listaAberta) return;
        e.preventDefault();
        if (this.indiceDestaque < 0) return;
        if (this.mostrarOpcaoVazia && this.indiceDestaque === 0) {
          this.selecionarValor("");
          return;
        }
        const idx = this.mostrarOpcaoVazia ? this.indiceDestaque - 1 : this.indiceDestaque;
        const opt = this.opcoesFiltradas[idx];
        if (opt != null) this.selecionarOpcao(opt);
      }
    },
    selecionarValor(valor) {
      this.$emit("update:modelValue", valor);
      this.termoBusca = "";
      this.fecharLista();
      this.focado = false;
      this.$refs.inputEl?.blur();
    },
    selecionarOpcao(opt) {
      this.selecionarValor(this.valorOpcao(opt));
    },
    limparSelecao() {
      this.selecionarValor("");
    },
  },
};
</script>

<style scoped>
.busca-autocomplete {
  position: relative;
  width: 100%;
}

.busca-autocomplete-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.busca-autocomplete-input {
  width: 100%;
  padding: 0.45rem 0.55rem;
  padding-right: 1.75rem;
  border: 1px solid var(--cor-separador);
  border-radius: 8px;
  font-size: 0.88rem;
  background: var(--cor-bg);
  color: var(--cor-fonte);
}

.busca-autocomplete-input:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.busca-autocomplete-input:focus {
  outline: none;
  border-color: var(--cor-primaria-media);
  box-shadow: 0 0 0 2px var(--cor-primaria-fraca, rgba(0, 0, 0, 0.06));
}

.busca-autocomplete-limpar {
  position: absolute;
  right: 0.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--cor-fonte-fraca);
  cursor: pointer;
}

.busca-autocomplete-limpar:hover {
  color: var(--cor-fonte);
  background: var(--cor-separador);
}

.busca-autocomplete-lista {
  position: absolute;
  z-index: 40;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  max-height: 14rem;
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
  overflow-y: auto;
  border: 1px solid var(--cor-separador);
  border-radius: 8px;
  background: var(--cor-bg);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.busca-autocomplete-item {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.4rem 0.55rem;
  cursor: pointer;
  font-size: 0.86rem;
  color: var(--cor-fonte);
}

.busca-autocomplete-item--ativa,
.busca-autocomplete-item:hover {
  background: var(--cor-primaria-fraca, rgba(0, 0, 0, 0.04));
}

.busca-autocomplete-item--estado {
  cursor: default;
  color: var(--cor-fonte-fraca);
  font-style: italic;
}

.busca-autocomplete-item--estado:hover {
  background: transparent;
}

.busca-autocomplete-item-label {
  font-weight: 500;
}

.busca-autocomplete-item-meta {
  font-size: 0.78rem;
  color: var(--cor-fonte-fraca);
}
</style>
